import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FiChevronDown, FiChevronUp, FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import logo from "../../../assets/image/mypage/wizlogo.svg";
import imo1 from "../../../assets/image/mypage/Frame 1261157087.svg";
import imo2 from "../../../assets/image/mypage/Frame 1261157084.svg";
import { sendToApp } from "../../../bridges/appBridge";

// ===== utils =====
const parseDateSafe = (v) => {
    if (!v) return null;
    let s = String(v).trim();
    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(s)) s = s.replace(" ", "T");
    const d = new Date(s);
    return isNaN(d) ? null : d;
};

// 개월 더하기 (말일 보정)
const addMonthsClamped = (date, months) => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();
    const target = new Date(y, m + months, 1);
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(d, lastDay));
    target.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
    return target;
};

const fmtYYMMDD = (d) => {
    if (!d) return "-";
    const yy = String(d.getFullYear()).slice(-2);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yy}.${mm}.${dd}`;
};

const calcDDay = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    const now = new Date();
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
};

// ===== component =====
const MyPlanCancel = () => {
    const navigate = useNavigate();

    const [showPlanInfo, setShowPlanInfo] = useState(false);
    const [planList, setPlanList] = useState([]);
    const [paymentList, setPaymentList] = useState([]);
    const [loading, setLoading] = useState(true);

    // 네이티브 IAP 준비 신호 (필요 시 유지)
    useEffect(() => {
        const sendReady = () => {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "WEB_IAP_READY" }));
        };
        sendReady();
        window.addEventListener("pageshow", sendReady);
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") sendReady();
        });
        return () => {
            window.removeEventListener("pageshow", sendReady);
        };
    }, []);

    // API
    const fetchPlans = async () => {
        const res = await axios.get(`${process.env.REACT_APP_FASTAPI_ADS_URL}/plan/list`);
        setPlanList(res.data || []);
    };

    const fetchPaymentList = useCallback(async () => {
        const user_id = localStorage.getItem("user_id");
        const res = await axios.get(`${process.env.REACT_APP_FASTAPI_ADS_URL}/ticket/list`, { params: { user_id } });
        setPaymentList(res.data || []);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                await Promise.all([fetchPlans(), fetchPaymentList()]);
            } catch (e) {
                console.error("[MyPlanCancel] fetch error", e);
            } finally {
                setLoading(false);
            }
        })();
    }, [fetchPaymentList]);

    const plans = useMemo(() => (Array.isArray(planList) ? planList : []), [planList]);

    // 최신 구독 1건 (결제내역: [name, amount, cycle, paidAt, endAt, token])
    const latestSub = useMemo(() => {
        const list = Array.isArray(paymentList) ? paymentList : [];
        const rows = list
            .filter((r) => [1, 12].includes(Number(r?.[2])))
            .map((r) => ({
                name: String(r?.[0] ?? "-"),
                amount: Number(r?.[1]) || 0,
                cycle: Number(r?.[2]) || 0,
                paidAt: parseDateSafe(r?.[3]),
                endAt: parseDateSafe(r?.[4]),
            }))
            .filter((r) => r.paidAt)
            .sort((a, b) => b.paidAt - a.paidAt);
        return rows[0] || null;
    }, [paymentList]);

    const tokenAmountForCycle = useMemo(() => {
        if (!latestSub) return null;
        const list = Array.isArray(paymentList) ? paymentList : [];
        const sameCycleRows = list
            .filter((r) => Number(r?.[0]) === latestSub.cycle)
            .map((r) => ({ token: r?.[5] != null ? Number(r?.[5]) : null, paidAt: parseDateSafe(r?.[3]) }))
            .filter((x) => x.paidAt && x.token != null)
            .sort((a, b) => b.paidAt - a.paidAt);
        return sameCycleRows[0]?.token ?? null;
    }, [paymentList, latestSub]);

    const planForCycle = useMemo(() => {
        if (!latestSub) return null;
        const arr = Array.isArray(plans) ? plans : [];
        return arr.find((p) => Number(p.BILLING_CYCLE) === latestSub.cycle) || null;
    }, [plans, latestSub]);

    const currentPlan = useMemo(() => {
        if (!latestSub) return null;
        return {
            name: planForCycle?.TICKET_NAME ?? latestSub.name,
            amount: Number(planForCycle?.TICKET_PRICE ?? latestSub.amount) || 0,
            cycle: latestSub.cycle,
            tokens: tokenAmountForCycle,
            paidAt: latestSub.paidAt,
            endAt: latestSub.endAt,
        };
    }, [latestSub, planForCycle, tokenAmountForCycle]);

    const planPeriod = useMemo(() => {
        if (!currentPlan) return null;
        const start = currentPlan.paidAt;
        const months = currentPlan.cycle === 12 ? 12 : 1;
        const next = addMonthsClamped(start, months);
        const end = new Date(next.getTime());
        end.setDate(end.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        return { start, end };
    }, [currentPlan]);

    const onCancelSubscribe = () => sendToApp("MANAGE_SUBSCRIPTION");
    const navPlan = () => navigate("/myplan");

    // 특장점 세트
    const FeatureList = ({ planName }) => {
        const name = (planName || "").toLowerCase();
        let features = [];
        if (name.includes("premium") || name.includes("프리미엄")) {
            features = [
                "AI토큰 월 100개",
                "년간 플랜 구매 시 30% 할인",
                "AI Report + 상권·입지분석 제공",
                "AI 사진 배경제거, 배경변경, 이미지 필터 제공",
            ];
        } else if (name.includes("standard") || name.includes("스탠다드")) {
            features = [
                "AI토큰 월 60개",
                "년간 플랜 구매 시 20% 할인",
                "AI Report 제공",
                "AI 사진 배경제거, 배경변경",
            ];
        } else {
            features = ["AI Report 제공", "AI 사진 배경제거, 배경변경"]; // 베이직 등
        }
        return (
            <ul className="mt-2 list-none text-sm text-gray-800">
                {features.map((txt, i) => (
                    <li key={i} className="relative pl-4 before:content-['•'] before:absolute before:left-0 before:top-0.5 before:text-base before:text-gray-500">
                        {txt}
                    </li>
                ))}
            </ul>
        );
    };

    if (loading) {
        return (
            <div className="bg-white h-dvh px-3 pt-6">
                <div className="flex items-center justify-start pb-6">
                    <button onClick={navPlan} className="px-2"><FiArrowLeft className="w-6 h-6 translate-y-1" /></button>
                    <h1 className="text-xl font-bold">플랜 해지</h1>
                </div>
                <div className="p-4 text-sm text-gray-500">불러오는 중…</div>
            </div>
        );
    }

    return (
        <div className="bg-white h-dvh px-3 pt-6">
            {/* 헤더 */}
            <div className="flex items-center justify-start pb-6">
                <div className="px-2">
                    <button onClick={navPlan}><FiArrowLeft className="w-6 h-6 justify-center translate-y-1" /></button>
                </div>
                <h1 className="text-xl font-bold">플랜 해지</h1>
            </div>

            <div className="pt-2 space-y-4">
                {/* 상단 경고 섹션 */}
                {currentPlan && (
                    <section className="text-center">
                        <img src={imo1} alt="emoji" className="w-20 h-20 mx-auto pb-3" />
                        <p className="text-base font-bold leading-6 text-black">
                            <span className="font-extrabold">{currentPlan.name}</span>을 해지하시면
                            <br />아래 혜택들은 이용할 수 없게 돼요.
                            <br />그래도 해지하시겠어요?
                        </p>
                    </section>
                )}

                {/* 플랜 카드 */}
                {currentPlan && (
                    <section
                        className="rounded-2xl bg-white
                        shadow-[0_12px_40px_rgba(0,0,0,0.16),0_2px_10px_rgba(0,0,0,0.08)]
                        px-8 py-2">
                        <h3 className="text-base font-bold text-black">{currentPlan.name}</h3>
                        <FeatureList planName={currentPlan.name} />
                    </section>

                )}

                {/* 남은 기간 안내 */}
                {currentPlan && (
                    <section className="text-center">
                        <img src={imo2} alt="emoji2" className="w-20 h-20 mx-auto mb-3" />
                        <p className="text-sm font-bold text-black">멤버쉽 이용기간이</p>
                        <p className="text-sm font-bold text-black">아직 <span className="text-[#5950FF] underline">{(() => { const d = calcDDay(planPeriod?.end); return d != null ? `${Math.max(d, 0)}일` : "-"; })()}</span> 이나 남았어요!</p>
                        <p className="text-sm text-gray-700">더 이용해보고 결정하시겠어요?</p>
                    </section>
                )}

                {/* 이용기간 배지 카드 */}
                {currentPlan && (
                    <section className="rounded-xl bg-white shadow-[2px_2px_10px_0_rgba(0,0,0,0.04)] px-16 pb-4 flex items-center justify-center gap-3">
                        <div className="w-8 h-8 rounded-lg overflow-hidden grid place-items-center">
                            <img src={logo} alt="logo" className="w-full h-full object-cover border" />
                        </div>
                        <div className="min-w-0 flex-1 flex items-center">
                            <div className="text-xs leading-4 font-semibold text-black">플랜 이용기간</div>
                            <div className="text-sm leading-6 font-bold text-black ml-auto">
                                {planPeriod ? `${fmtYYMMDD(planPeriod.start)}~${fmtYYMMDD(planPeriod.end)}` : "-"}
                            </div>
                        </div>

                    </section>
                )}

                {/* 유의사항 (아코디언) */}
                <section className="text-sm pb-4 px-2">
                    <button className="w-full flex justify-between items-center font-semibold text-black" onClick={() => setShowPlanInfo(!showPlanInfo)}>
                        플랜 구독 유의사항 {showPlanInfo ? <FiChevronUp /> : <FiChevronDown />}
                    </button>
                    {showPlanInfo && (
                        <div className="pt-2 text-black space-y-3">
                            <div>
                                <h3 className="font-semibold text-black">플랜 변경</h3>
                                <p className="pt-1">[낮은 가격으로 변경(다운 그레이드)]</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    {[
                                        "다음 결제일부터 적용됩니다. 그 전까지는 기존 플랜 혜택 유지합니다.",
                                        "이미 지급된 토큰은 월 말일까지 사용 가능, 다음 사이클부터 하위 플랜 기준으로 지급됩니다.",
                                    ].map((txt, i) => (
                                        <li key={`down-${i}`}>{txt}</li>
                                    ))}
                                </ul>
                                <p className="pt-1">[높은 가격으로 변경(업그레이드)]</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    {["결제 승인 즉시 상위 플랜 혜택 사용할 수 있습니다."].map((txt, i) => (
                                        <li key={`up-${i}`}>{txt}</li>
                                    ))}
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-semibold text-black">플랜 해지</h3>
                                <p className="pt-1">[해지 방법]</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    {[
                                        "구독 해지는 각 스토어의 구독 관리 화면에서만 가능합니다. (앱의 버튼은 스토어 화면으로 이동만 지원)",
                                        "해지 효력 시점: 해지 후 다음 갱신일부터 과금이 중단됩니다. 만료일(갱신 전날)까지는 기존 혜택을 이용하실 수 있습니다.",
                                    ].map((txt, i) => (
                                        <li key={`cancel-${i}`}>{txt}</li>
                                    ))}
                                </ul>
                                <p className="pt-1">[토큰/이용권]</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    {[
                                        "매월 지급되는 구독 토큰은 구독이 끝나는 날까지 유효합니다. 해지하더라도 보유 토큰은 만료일까지 사용 가능하며, 차감은 유효기간이 빠른 토큰부터 진행됩니다.",
                                    ].map((txt, i) => (
                                        <li key={`token-${i}`}>{txt}</li>
                                    ))}
                                </ul>
                                <p className="pt-1">[계정/데이터]</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    {[
                                        "구독 해지는 계정 탈퇴가 아닙니다. 생성한 콘텐츠와 기록은 정책에 따라 보존되며, 계정 탈퇴 시 삭제될 수 있습니다.",
                                    ].map((txt, i) => (
                                        <li key={`account-${i}`}>{txt}</li>
                                    ))}
                                </ul>
                            </div>


                        </div>
                    )}
                </section>
                {/* 하단 고정 버튼 */}
            </div>

            <div className="sticky bottom-0 left-0 right-0 bg-white px-4 py-3 border-t">
                <div className="space-y-2">
                    <button onClick={navPlan} className="bg-[#5950FF] text-white w-full py-3 rounded-lg text-center font-bold">
                        플랜 유지
                    </button>
                    <button onClick={onCancelSubscribe} className="w-full py-3 rounded-lg text-center font-bold border border-[#5950FF] text-[#5950FF]">
                        플랜 해지
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MyPlanCancel;
